// SlidePreview.jsx
// This component is used to display a preview of a slide in the file viewer.

// Copyright HS Analysis GmbH, 2025
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// External imports
import { LazyLoadImage } from "react-lazy-load-image-component";

// MUI imports
import { makeStyles } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";

// HSA Imports
import Backend from "../../common/utils/Backend";
import ValidatedImage from "../../viewer/components/ValidatedImage";

const useStyles = makeStyles((theme) => ({
  root: {
    pointerEvents: "none",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
  },
  tagBox: {
    display: "inline-block",
    width: 20,
    height: 20,
    borderRadius: 5,
    color: "#fff",
    border: `1px solid ${theme.palette.primary.inactive}`,
    margin: 2,
  },
  row: {
    cursor: "pointer",
  },
  slide: {
    position: "relative",
    margin: "10px",
    width: "280px",
    height: "100px",
    border: "1px solid #c2c8cb",
    boxShadow: `2px 2px 4px ${theme.palette.primary.inactive}`,
    background: "#c2c8cb",
  },
  inactiveSlide: {
    border: `3px solid ${theme.palette.primary.inactive}`,
    background: "#c2c8cb",
    margin: "3px 0",
    "&:hover >div": {
      backgroundColor: theme.palette.selected.main,
    },
    // Add touch interaction styles
    "@media (hover: none)": {
      "&:active >div": {
        backgroundColor: theme.palette.selected.main,
      },
    },
  },
  activeSlide: {
    border: `3px solid ${theme.palette.primary.main}`,
    background: "#c2c8cb",
    margin: "3px 0",
  },
  splitScreenSlide: {
    border: `3px dashed ${theme.palette.primary.main}`,
    background: "#c2c8cb",
    margin: "3px 0",
  },
  textfield: {
    position: "relative",
    marginTop: 12,
    marginLeft: 5,
    height: "100px",
    width: "150px",
    background: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    boxShadow: `inset 1px 0px 6px ${theme.palette.primary.inactive}`,
    padding: "5px",
    textAlign: "center",
    overflow: "hidden",
    fontSize: "x-small",
  },
  slideFileName: {
    width: "100%",
    wordWrap: "break-word", // Ensures long text wraps
    overflowWrap: "break-word", // Alternative for better compatibility
    textAlign: "center", // Optional: centers the text
  },
  slideAdditionalName: {
    width: "100%",
    position: "absolute",
    top: 0,
    textAlign: "center",
  },
  slideComment: {
    width: "100%",
    position: "absolute",
    bottom: 0,
    textAlign: "center",
  },
  infoLabel: {
    display: "flex",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    color: "#808080",
  },
  placeholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  "slide-corner": {
    "&::before": {
      position: "absolute",
      content: '""',
      display: "block",
      width: "15px",
      height: "30px",
      background: "#c2c8cb",
      border: "0 solid #c2c8cb",
      boxShadow: `2px 2px 4px ${theme.palette.primary.inactive}`,
    },
    "&::after": {
      position: "absolute",
      content: '""',
      display: "block",
      width: "15px",
      height: "30px",
      background: "#c2c8cb",
      border: "0 solid #c2c8cb",
      boxShadow: `2px 2px 4px ${theme.palette.primary.inactive}`,
    },
  },
  "top-corners": {
    "&::before": {
      borderRadius: "0 0 30px 0",
      borderRightWidth: "2px",
      borderBottomWidth: "10px",
    },
    "&::after": {
      right: "0",
      borderRadius: "0 0 0 30px",
      borderLeftWidth: "2px",
      borderBottomWidth: "10px",
    },
  },
  "bottom-corners": {
    "&::before": {
      bottom: "0",
      borderRadius: "0 30px 0 0",
      borderTopWidth: "10px",
      borderRightWidth: "2px",
    },
    "&::after": {
      right: "0",
      bottom: "0",
      borderRadius: "30px 0 0 0",
      borderTopWidth: "10px",
      borderLeftWidth: "2px",
    },
  },
  "slide-content": {
    position: "absolute",
    left: "5px",
    top: "5px",
    width: "270px",
    height: "90px",
    background: "white",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    justifyContent: "center",
    "& > div": {
      height: 90,
      marginLeft: 10,
    },
  },
  "slide-thumbnail": {
    display: "block",
    margin: "auto",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    borderRadius: "5px",
  },
}));

const SlidePreview = ({
  fileStatus,
  file,
  containerWidth,
  showAdditionalName,
  showComments,
}) => {
  const classes = useStyles();

  let fileStatusClass = classes.inactiveSlide;
  if (fileStatus === "imgActive") {
    fileStatusClass = classes.activeSlide;
  } else if (fileStatus === "imgSplitScreen") {
    fileStatusClass = classes.splitScreenSlide;
  }

  return (
    <div className={fileStatusClass}>
      <div className={classes.root}>
        <div className={classes.textfield}>
          {/* Top line: additionalName */}
          {showAdditionalName && file.additionalName && (
            <div className={classes.slideAdditionalName}>
              {file.additionalName}
            </div>
          )}
          <span className={classes.slideFileName}>
            {file.name}
            {file.sceneCount > 1 && ", S" + (file.scene + 1)}
          </span>
          {/* Bottom line: comment */}
          {showComments && file.comment && (
            <div className={classes.slideComment}>{file.comment}</div>
          )}
        </div>
        <div style={{ marginRight: 26 }}>
          <div className={classes.slide}>
            <div className={classes["slide-content"]}>
              <div>
                <ValidatedImage
                  draggable="false"
                  src={Backend.renderSlideLabel(file.id)}
                  height={90}
                />
              </div>
              <div>
                <LazyLoadImage
                  className={classes["slide-thumbnail"]}
                  src={
                    containerWidth < 900
                      ? Backend.renderThumbnail(encodeURIComponent(file.id))
                      : Backend.renderSlideImg(encodeURIComponent(file.id))
                  }
                  alt="Slide Thumbnail"
                  placeholder={
                    <div className={classes.placeholder}>
                      <CircularProgress />
                    </div>
                  }
                />
              </div>
            </div>
            <div
              className={`${classes["slide-corner"]} ${classes["top-corners"]}`}
            ></div>
            <div
              className={`${classes["slide-corner"]} ${classes["bottom-corners"]}`}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

SlidePreview.propTypes = {
  fileStatus: PropTypes.string.isRequired,
  file: PropTypes.object.isRequired,
  containerWidth: PropTypes.number.isRequired,
  showAdditionalName: PropTypes.bool,
  showComments: PropTypes.bool,
};

export default SlidePreview;
